/**
 * Tooltip — small dark bubble shown on hover/focus of the wrapped trigger.
 *
 * Renders the bubble through a portal with `position: fixed`, computed from
 * the trigger's bounding rect. This is required because table cells in this
 * app sit inside `overflow-hidden` / `overflow-x-auto` wrappers — a CSS-only
 * absolutely-positioned tooltip would get clipped at those boundaries.
 *
 * Usage:
 *   <Tooltip content="Edit">
 *     <button aria-label="Edit user">…</button>
 *   </Tooltip>
 *
 *   <Tooltip content={…} interactive hideDelayMs={150} side="bottom" multiline>
 *     …
 *   </Tooltip>
 */
import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils/helpers'

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right'

export interface TooltipProps {
    content: ReactNode
    children: ReactNode
    side?: TooltipSide
    /** Allow the bubble to wrap onto multiple lines (default: single line, nowrap). */
    multiline?: boolean
    className?: string
    arrowClassName?: string
    /**
     * When true, the bubble uses `pointer-events-auto` and a short hide delay so the
     * cursor can move into the tooltip (e.g. scroll HTML body). Default false keeps the
     * original immediate hide + non-interactive bubble.
     */
    interactive?: boolean
    /** Hide delay when `interactive` is true (ignored otherwise). Default 120ms. */
    hideDelayMs?: number
}

interface Coords {
    top: number
    left: number
}

// Px gap between trigger and bubble (matches tailwind mb-1.5 / mt-1.5 / etc).
const GAP = 6

const ARROW_POSITION: Record<TooltipSide, string> = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-1',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-1',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-1',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-1',
}

const SIDE_TRANSFORM: Record<TooltipSide, string> = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
}

function computeAnchor(rect: DOMRect, side: TooltipSide): Coords {
    switch (side) {
        case 'top':
            return { top: rect.top - GAP, left: rect.left + rect.width / 2 }
        case 'bottom':
            return { top: rect.bottom + GAP, left: rect.left + rect.width / 2 }
        case 'left':
            return { top: rect.top + rect.height / 2, left: rect.left - GAP }
        case 'right':
            return { top: rect.top + rect.height / 2, left: rect.right + GAP }
    }
}

export function Tooltip({
    content,
    children,
    side = 'top',
    multiline = false,
    className,
    arrowClassName,
    interactive = false,
    hideDelayMs = 120,
}: TooltipProps) {
    const triggerRef = useRef<HTMLSpanElement>(null)
    const [coords, setCoords] = useState<Coords | null>(null)
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const clearHideTimer = useCallback(() => {
        if (hideTimerRef.current !== null) {
            clearTimeout(hideTimerRef.current)
            hideTimerRef.current = null
        }
    }, [])

    useEffect(() => () => clearHideTimer(), [clearHideTimer])

    const hideImmediate = useCallback(() => {
        clearHideTimer()
        setCoords(null)
    }, [clearHideTimer])

    const scheduleHide = useCallback(() => {
        if (!interactive) {
            hideImmediate()
            return
        }
        clearHideTimer()
        hideTimerRef.current = setTimeout(() => {
            hideTimerRef.current = null
            setCoords(null)
        }, hideDelayMs)
    }, [interactive, hideDelayMs, clearHideTimer, hideImmediate])

    const show = useCallback(() => {
        clearHideTimer()
        const el = triggerRef.current
        if (!el) return
        setCoords(computeAnchor(el.getBoundingClientRect(), side))
    }, [side, clearHideTimer])

    const visible = coords !== null

    return (
        <>
            <span
                ref={triggerRef}
                className="relative inline-flex"
                onMouseEnter={show}
                onMouseLeave={scheduleHide}
                onFocus={show}
                onBlur={scheduleHide}
            >
                {children}
            </span>
            {visible &&
                typeof document !== 'undefined' &&
                createPortal(
                    <span
                        role="tooltip"
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: SIDE_TRANSFORM[side],
                        }}
                        onMouseEnter={interactive ? clearHideTimer : undefined}
                        onMouseLeave={interactive ? scheduleHide : undefined}
                        className={cn(
                            interactive ? 'pointer-events-auto' : 'pointer-events-none',
                            'z-[10000] rounded-md bg-slate-800 px-2 py-1 text-xs font-medium text-white shadow-lg',
                            multiline
                                ? 'max-w-xs whitespace-pre-line text-left'
                                : 'whitespace-nowrap',
                            className,
                        )}
                    >
                        {content}
                        <span
                            aria-hidden="true"
                            className={cn(
                                'absolute h-2 w-2 rotate-45 bg-slate-800',
                                ARROW_POSITION[side],
                                arrowClassName,
                            )}
                        />
                    </span>,
                    document.body,
                )}
        </>
    )
}
